import { IQuery, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import { ConditionalStrategy } from 'utility-ts-strategy';
import { APTerm } from '../../../../global/builders/ap_term_builder';
import { APUserModeConfig } from '../../../../global/components/user_mode_config/config/ap_user_mode_config';
import { IAPTermsDatasource } from '../../../../global/datasources/ap_terms_datasource';
import { APModel } from '../../../../global/models/ap_model';
import { APTermServant } from '../../../../global/servants/ap_term_servant';
import {
  FeedbackSuggestedPlanModel,
  feedbackSuggestedPlanStatus,
  IFeedbackSuggestedPlanModel
} from '../models/feedback_suggested_plan_model';
import { PlanFeedbackServant } from '../servants/plan_feedback_servant';

const PlanStatusStyleStrategy = new ConditionalStrategy({
  'plan-editing': (plan: { status: feedbackSuggestedPlanStatus }) => PlanFeedbackServant.isPlanEditing(plan),
  'plan-pending': (plan: { status: feedbackSuggestedPlanStatus }) => PlanFeedbackServant.isPlanSentToStudent(plan),
  'plan-viewing': (plan: { status: feedbackSuggestedPlanStatus }) => PlanFeedbackServant.isPlanReadOnlyMode(plan),
  'clr-orange_40-bkg': (plan: { status: feedbackSuggestedPlanStatus }) => PlanFeedbackServant.isPlanBlocked(plan)
});

export class AdvisorHeaderDetailsQuery implements IQuery {
  private __result: {
    advisorName: string;
    submittedDate: string;
    status: feedbackSuggestedPlanStatus;
    updatedTimeStamp: string;
    showCommittedLabel: boolean;
    showCreateSuggestion: boolean;
    showRevokeButton: boolean;
    showPlanLooksGood: boolean;
    disableRevoke: boolean;
    hasAdvisorPlan: boolean;
    hasStudentPlan: boolean;
    planStatusStyle: string;
    planEditedTemplate: string;
    planUpdatedTemplate: string;
    planCreatedTemplate: string;
  };

  constructor(
    QueryMediator: IQueryMediator,
    private ngAPTermsDatasource: IAPTermsDatasource,
    private ngAPModel: APModel,
    private ngAPUserModeConfig: APUserModeConfig,
    private ngFeedbackSuggestedPlanModel: FeedbackSuggestedPlanModel
  ) {
    QueryMediator.registerQuery(this, ngFeedbackSuggestedPlanModel, ngAPTermsDatasource);
  }

  private lastEditBy(details: IFeedbackSuggestedPlanModel) {
    return `${_4.get(details, 'edited_by.first_name', ' ')}  ${_4.get(details, 'edited_by.last_name', '')}`;
  }
  private planUpdatedTemplateText(details: IFeedbackSuggestedPlanModel): string {
    if (details.updated_ts) {
      return `${this.lastEditBy(details)} on ${moment(details.updated_ts).format('MMMM DD, YYYY - H:mm A')}`;
    }
    return `Never`;
  }
  private planEditedTemplateText(details: IFeedbackSuggestedPlanModel): string {
    if (details.edited_ts) {
      return `Last Edit By: <strong>${this.lastEditBy(details)} on ${moment(details.edited_ts).format(
        'MMMM DD, YYYY - H:mm A'
      )}</strong>`;
    }
    return ``;
  }

  run(): this {
    const details = this.ngFeedbackSuggestedPlanModel.read();
    const termsWithMeta = this.ngAPTermsDatasource.metasource().derived();
    const futureTerms = APTermServant.getFutureTerms(termsWithMeta);
    const plannedTerms = _4.filter(futureTerms, (term: APTerm) =>
      _4.includes(this.ngAPModel.read().planned_term, term.id)
    );
    const hasStudentPlan = plannedTerms.length > 0;
    const hasAdvisorPlan = !_4.isEmpty(details.id);
    const isAdvisor = this.ngAPUserModeConfig.isAdvisor();
    const isSuggestedPlanPending = _4.includes(['COMMITTED', 'RESOLVING'], details.status);
    const isSuggestedPlanInactive = !_4.includes(['COMMITTED', 'EDITING', 'RESOLVING', 'BLOCKED'], details.status);
    const advisorName = `${_4.get(details, 'committed_by.first_name', ' ')}  ${_4.get(
      details,
      'committed_by.last_name',
      ''
    )}`;
    const planUpdatedTemplate = `Plan Last Updated By: <strong>${this.planUpdatedTemplateText(details)}</strong>`;

    const planEditedTemplate = this.planEditedTemplateText(details);

    this.__result = {
      hasAdvisorPlan,
      hasStudentPlan,
      advisorName,
      submittedDate: moment(details.updated_ts).format('MMM DD, YYYY'),
      updatedTimeStamp: moment(details.updated_ts).format('MMMM DD, YYYY'),
      status: details.status,
      showCommittedLabel: isAdvisor && isSuggestedPlanPending,
      showCreateSuggestion: isAdvisor && isSuggestedPlanInactive,
      showRevokeButton: isAdvisor && isSuggestedPlanPending,
      showPlanLooksGood: isAdvisor && isSuggestedPlanInactive,
      disableRevoke: hasAdvisorPlan && _4.includes(['RESOLVING'], details.status),
      planStatusStyle: PlanStatusStyleStrategy.evaluate(details),
      planEditedTemplate,
      planUpdatedTemplate,
      planCreatedTemplate: `Created: <strong>${moment(details.updated_ts).format('MMMM DD, YYYY')}</strong>`
    };
    return this;
  }
  get result() {
    return this.__result;
  }
}

angular.module('capability-ng-academic-planner').service('ngAPAdvisorHeaderDetailsQuery', AdvisorHeaderDetailsQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/suggestions/components/plan_feedback/queries/advisor_plan_details_query.ts